import { useLayoutEffect } from 'react';

import { MAIN_CONTENT_AREA_ID } from 'layout/MainLayout/useContentAreaRect';

/** Below this the rows would be unusable, so a very short viewport lets the page scroll instead. */
const MIN_TABLE_HEIGHT = 256;

/** Page content after the list, as opposed to the layout's own trailing footer and padding. */
const hasContentAfter = (element: HTMLElement): boolean => {
    for (let next = element.nextElementSibling; next; next = next.nextElementSibling) {
        const { position } = getComputedStyle(next);
        if (position !== 'fixed' && position !== 'absolute' && next.getBoundingClientRect().height > 0) return true;
    }
    return false;
};

/**
 * Keeps the page content area from scrolling on a list page, so the title, summary cards and toolbar
 * stay put and only the rows scroll, inside the table. The table keeps the height its own CSS gives
 * it (the growing child of the page's column) and is only shortened by however much the page would
 * otherwise overflow — a page whose chrome takes more room than its layout budgets for.
 *
 * Only for a page whose one list is its last content, with nothing scrolling in between: two lists
 * would each claim the same room, a list with more page content under it belongs to a page that
 * scrolls, and a list inside its own scrolling panel or a modal keeps the scroll it has.
 */
const lists = new Set<() => void>();

export const useListFitsContentArea = (table: HTMLElement | null): void => {
    useLayoutEffect(() => {
        const area = document.getElementById(MAIN_CONTENT_AREA_ID);
        if (!table || !area?.contains(table)) return;

        const chain: HTMLElement[] = [];
        for (let element: HTMLElement | null = table; element && element !== area; element = element.parentElement) {
            chain.push(element);
        }
        // The chain's last element is the page itself; what follows it is the layout's footer.
        const eligible = () =>
            !chain.slice(1).some((element) => /auto|scroll/.test(getComputedStyle(element).overflowY)) &&
            !chain.slice(0, -1).some(hasContentAfter);

        const fit = () => {
            // Measured from the table's own CSS height every time, so a taller viewport gives the
            // room back rather than keeping the height the page last had to be squeezed into. That
            // height can be all of its rows, which clamps the rows' scroll position to the top during
            // the measurement; it is put back, or a list scrolled while the chrome around it changes
            // height (the filter bar wrapping onto one row less) would jump back to its first rows.
            const { scrollTop } = table;
            table.style.removeProperty('height');
            const overflow = area.scrollHeight - area.clientHeight;
            if (overflow > 0) {
                const height = Math.max(MIN_TABLE_HEIGHT, table.getBoundingClientRect().height - overflow);
                table.style.setProperty('height', `${height}px`);
            }
            table.scrollTop = scrollTop;
        };

        // Every list in the content area registers, fitting or not, so a second list switches the first off.
        const refresh = () => {
            if (lists.size === 1 && eligible()) fit();
            else table.style.removeProperty('height');
        };

        // Anything around the table changing height (cards loading, a filter row wrapping, the totals
        // slot, the window) resizes the content area or one of the siblings along the chain.
        // The refit is deferred to the next frame: setting the table's height from inside the callback
        // resizes the observed elements along the chain in the same frame, which the browser reports as
        // "ResizeObserver loop completed with undelivered notifications". One pending frame covers a burst.
        let frame = 0;
        const resized = new ResizeObserver(() => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                frame = 0;
                refresh();
            });
        });
        resized.observe(area);
        for (const element of chain) {
            for (const sibling of element.parentElement?.children ?? []) {
                if (sibling !== table) resized.observe(sibling);
            }
        }
        // The layout's footer can mount after the list has already fitted itself.
        const added = new MutationObserver(() => {
            for (const child of area.children) resized.observe(child);
            refresh();
        });
        added.observe(area, { childList: true });
        lists.add(refresh);
        lists.forEach((listRefresh) => listRefresh());

        return () => {
            resized.disconnect();
            cancelAnimationFrame(frame);
            added.disconnect();
            lists.delete(refresh);
            table.style.removeProperty('height');
            lists.forEach((listRefresh) => listRefresh());
        };
    }, [table]);
};
